import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Truck,
  RotateCcw,
  Pen,
  Check,
  Clock,
  User,
  Package,
  MapPin,
  Phone,
  Calendar,
  FileText,
  Download,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONDITION_OPTIONS = ["good", "fair", "poor", "damaged"];

/** Small presentational block that renders one signature slot.
 *  Replaces nested ternaries so each state is clearly named. */
const SignatureBlock = ({ label, signature, canSign, awaitingReason, buttonLabel, onSign }) => {
  if (signature?.confirmed) {
    return (
      <div>
        <Label className="mb-2 block text-sm font-medium">{label}</Label>
        <div className="space-y-1">
          <div className="border rounded-sm p-2 bg-slate-50">
            <img src={signature.signature_image} alt={label} className="max-h-16" />
          </div>
          <p className="text-xs text-slate-500">Signed by: {signature.signed_by_name}</p>
        </div>
      </div>
    );
  }
  if (canSign) {
    return (
      <div>
        <Label className="mb-2 block text-sm font-medium">{label}</Label>
        <Button variant="outline" size="sm" onClick={onSign} className="w-full rounded-sm">
          <Pen className="h-3 w-3 mr-1" /> {buttonLabel}
        </Button>
      </div>
    );
  }
  return (
    <div>
      <Label className="mb-2 block text-sm font-medium">{label}</Label>
      <p className="text-xs text-slate-400">{awaitingReason}</p>
    </div>
  );
};

const RentalJobCard = () => {
  const { rentalId } = useParams();
  const navigate = useNavigate();
  const [jobCard, setJobCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [signDialog, setSignDialog] = useState({ open: false, section: null, signer: null });
  const [signName, setSignName] = useState("");
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const [workers, setWorkers] = useState([]);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and axios are module-level constants; availableWorkers defined inside callback
  const fetchJobCard = useCallback(async () => {
    try {
      const [jcRes, userRes] = await Promise.all([
        axios.get(`${API}/rental-job-cards/${rentalId}`, { withCredentials: true }),
        axios.get(`${API}/auth/me`, { withCredentials: true }),
      ]);
      setJobCard(jcRes.data);
      setCurrentUser(userRes.data);
      
      // Fetch workers list for admin/manager to assign
      if (userRes.data?.role === "admin" || userRes.data?.role === "manager") {
        try {
          const workersRes = await axios.get(`${API}/users`, { withCredentials: true });
          const availableWorkers = workersRes.data.filter(
            (u) => u.is_active && (u.role === "worker" || u.role === "manager" || u.role === "admin")
          );
          setWorkers(availableWorkers);
        } catch (err) {
          console.log("Could not fetch workers list");
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error("Failed to load job card");
      }
    } finally {
      setLoading(false);
    }
  }, [rentalId]);

  const downloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/rental-job-cards/${rentalId}/pdf`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `rental_job_card_${rentalId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleAssignTechnician = async (workerId) => {
    if (!workerId || workerId === "unassigned") {
      return;
    }
    try {
      await axios.post(
        `${API}/rentals/${rentalId}/assign-technician`,
        { technician_id: workerId },
        { withCredentials: true }
      );
      toast.success("Technician assigned successfully");
      fetchJobCard(); // Refresh to get updated technician info
    } catch (error) {
      console.error("Error assigning technician:", error);
      toast.error(error.response?.data?.detail || "Failed to assign technician");
    }
  };

  useEffect(() => {
    fetchJobCard();
  }, [fetchJobCard]);

  // Canvas signature drawing
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#1e293b";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    setSignature(null);
  };

  const startDrawing = (e) => {
    isDrawingRef.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawingRef.current) {
      isDrawingRef.current = false;
      const canvas = canvasRef.current;
      setSignature(canvas.toDataURL("image/png"));
    }
  };

  const openSignDialog = (section, signer) => {
    setSignDialog({ open: true, section, signer });
    setSignName(signer === "technician" ? (currentUser?.name || "") : "");
    setSignature(null);
    setTimeout(initCanvas, 100);
  };

  const handleSign = async () => {
    if (!signature) return toast.error("Please draw your signature");
    if (!signName.trim()) return toast.error("Please enter your name");
    
    setSaving(true);
    try {
      const endpoint = signDialog.section === "delivery" ? "delivery-sign" : "return-sign";
      await axios.post(
        `${API}/rental-job-cards/${rentalId}/${endpoint}/${signDialog.signer}`,
        { signature_image: signature, signed_by_name: signName, confirmed: true },
        { withCredentials: true }
      );
      toast.success(`${signDialog.signer === "technician" ? "Technician" : "Customer"} signature recorded`);
      setSignDialog({ open: false, section: null, signer: null });
      fetchJobCard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save signature");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateField = async (field, value) => {
    try {
      await axios.put(
        `${API}/rental-job-cards/${rentalId}`,
        { [field]: value },
        { withCredentials: true }
      );
      setJobCard((prev) => ({ ...prev, [field]: value }));
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleScheduleReturn = async () => {
    try {
      await axios.post(`${API}/rental-job-cards/${rentalId}/schedule-return`, {}, { withCredentials: true });
      toast.success("Return scheduled");
      fetchJobCard();
    } catch (error) {
      toast.error("Failed to schedule return");
    }
  };

  const handleStartDelivery = async () => {
    try {
      await axios.post(`${API}/rental-job-cards/${rentalId}/start-delivery`, {}, { withCredentials: true });
      toast.success("Delivery started");
      fetchJobCard();
    } catch (error) {
      toast.error("Failed to start delivery");
    }
  };

  const getStatusConfig = (status) => {
    const config = {
      pending_delivery: { label: "Pending Delivery", color: "bg-amber-100 text-amber-800" },
      delivered: { label: "Delivered", color: "bg-blue-100 text-blue-800" },
      pending_return: { label: "Pending Return", color: "bg-purple-100 text-purple-800" },
      returned: { label: "Returned", color: "bg-emerald-100 text-emerald-800" },
      completed: { label: "Completed", color: "bg-emerald-200 text-emerald-900" },
    };
    return config[status] || config.pending_delivery;
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-64"></div>
          <div className="h-96 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="p-6 md:p-8 max-w-2xl mx-auto">
        <div className="text-center py-20 border border-slate-200 rounded-sm bg-slate-50">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Rental No Longer Exists</h2>
          <p className="text-slate-500 mb-2 max-w-sm mx-auto">
            This rental record has been removed from the system. The job card cannot be displayed.
          </p>
          <p className="text-xs text-slate-400 mb-6 font-mono">Rental ID: {rentalId}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate(-1)} className="rounded-sm">
              Go Back
            </Button>
            <Button onClick={() => navigate("/job-history")} className="rounded-sm bg-slate-900 hover:bg-slate-800">
              View Job History
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!jobCard) {
    return (
      <div className="p-6 md:p-8 text-center">
        <p className="text-slate-500">Job card not found</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  const statusConfig = getStatusConfig(jobCard.status);
  const isLocked = jobCard.is_locked;
  const isCustomer = currentUser?.role === "customer";
  const canEdit = !isLocked && !isCustomer && (currentUser?.role === "admin" || currentUser?.role === "manager" || currentUser?.role === "worker");
  // Customers can only sign in their signature blocks
  const canCustomerSign = isCustomer && !isLocked;
  const isDeliveryComplete = jobCard.delivery_technician_signature?.confirmed && jobCard.delivery_customer_signature?.confirmed;
  const isReturnComplete = jobCard.return_technician_signature?.confirmed && jobCard.return_customer_signature?.confirmed;

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid="rental-job-card-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Rental Job Card</h1>
            <p className="text-sm text-slate-500">{jobCard.rental_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={downloadPdf} data-testid="download-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
          <Badge className={`${statusConfig.color} rounded-sm text-sm`}>{statusConfig.label}</Badge>
        </div>
      </div>

      {/* Start Delivery - Prominent at top */}
      {canEdit && jobCard.status === "pending_delivery" && !isDeliveryComplete && (
        <Card className="rounded-sm border-blue-200 bg-blue-50 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Truck className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">Start Delivery</p>
                  <p className="text-sm text-blue-600">Begin the delivery process for this rental</p>
                </div>
              </div>
              <Button 
                onClick={handleStartDelivery} 
                className="rounded-sm bg-blue-600 hover:bg-blue-700"
                data-testid="start-delivery-btn"
              >
                <Truck className="h-4 w-4 mr-2" /> Start Delivery
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-6">
        {/* Venue banner — shown when a venue is linked */}
        {jobCard.venue_name && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-sm">
            <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-xs text-blue-500 font-medium uppercase tracking-wide">Service Venue</p>
              <p className="text-sm font-semibold text-blue-800">{jobCard.venue_name}</p>
            </div>
          </div>
        )}

        {/* Customer & Item Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="rounded-sm border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4 text-slate-500" />
                Customer Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Name</span>
                <span className="font-medium">{jobCard.customer_name}</span>
              </div>
              {jobCard.customer_phone && (
                <div className="flex justify-between">
                  <span className="text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</span>
                  <span>{jobCard.customer_phone}</span>
                </div>
              )}
              {jobCard.customer_address && (
                <div className="flex justify-between">
                  <span className="text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> Address</span>
                  <span className="text-right max-w-[200px] break-words">{jobCard.customer_address}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-sm border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4 text-slate-500" />
                Rental Item
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Item</span>
                <span className="font-medium">{jobCard.item_name}</span>
              </div>
              {jobCard.item_sku && (
                <div className="flex justify-between">
                  <span className="text-slate-500">SKU</span>
                  <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{jobCard.item_sku}</code>
                </div>
              )}
              {jobCard.item_description && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Description</span>
                  <span className="text-right max-w-[200px] break-words">{jobCard.item_description}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">Quantity</span>
                <span>{jobCard.quantity}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Dates & Technician */}
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-slate-400" />
                <span className="text-slate-500">Technician:</span>
                {(currentUser?.role === "admin" || currentUser?.role === "manager") && !isLocked ? (
                  <Select
                    value={jobCard.technician_id || "unassigned"}
                    onValueChange={handleAssignTechnician}
                  >
                    <SelectTrigger className="h-7 w-[160px] text-sm" data-testid="assign-technician-select">
                      <SelectValue placeholder="Assign technician" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Not assigned</SelectItem>
                      {workers.map((worker) => (
                        <SelectItem key={worker.user_id} value={worker.user_id}>
                          {worker.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="font-medium">{jobCard.technician_name || "Not assigned"}</span>
                )}
              </div>
              {jobCard.delivery_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-500">Delivery:</span>
                  <span>{jobCard.delivery_date}</span>
                </div>
              )}
              {jobCard.return_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-500">Expected Return:</span>
                  <span>{jobCard.return_date}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Delivery Section */}
        <Card className="rounded-sm border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="h-4 w-4 text-blue-500" />
                Delivery
              </CardTitle>
              {isDeliveryComplete && (
                <Badge className="bg-emerald-100 text-emerald-700 rounded-sm">
                  <Check className="h-3 w-3 mr-1" /> Complete
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Condition on delivery */}
            <div className="space-y-2">
              <Label className="text-sm">Condition on Delivery</Label>
              {canEdit && !isDeliveryComplete ? (
                <Select
                  value={jobCard.condition_on_delivery || "good"}
                  onValueChange={(v) => handleUpdateField("condition_on_delivery", v)}
                >
                  <SelectTrigger className="w-48 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDITION_OPTIONS.map((c) => (
                      <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="outline" className="rounded-sm capitalize">
                  {jobCard.condition_on_delivery || "Not recorded"}
                </Badge>
              )}
            </div>

            {/* Delivery Notes */}
            <div className="space-y-2">
              <Label className="text-sm">Delivery Notes</Label>
              {canEdit && !isDeliveryComplete ? (
                <Textarea
                  value={jobCard.delivery_notes || ""}
                  onChange={(e) => setJobCard((prev) => ({ ...prev, delivery_notes: e.target.value }))}
                  onBlur={(e) => handleUpdateField("delivery_notes", e.target.value)}
                  placeholder="Notes about the delivery..."
                  rows={2}
                  className="rounded-sm"
                />
              ) : (
                <p className="text-sm text-slate-600">{jobCard.delivery_notes || "No notes"}</p>
              )}
            </div>

            {/* Delivery Signatures */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <SignatureBlock
                label="Technician Signature"
                signature={jobCard.delivery_technician_signature}
                canSign={canEdit}
                awaitingReason="Awaiting signature"
                buttonLabel="Sign as Technician"
                onSign={() => openSignDialog("delivery", "technician")}
              />
              <SignatureBlock
                label="Customer Signature"
                signature={jobCard.delivery_customer_signature}
                canSign={(canEdit || canCustomerSign) && jobCard.delivery_technician_signature?.confirmed}
                awaitingReason={jobCard.delivery_technician_signature?.confirmed ? "Awaiting signature" : "Technician must sign first"}
                buttonLabel={isCustomer ? "Sign as Customer" : "Customer Sign"}
                onSign={() => openSignDialog("delivery", "customer")}
              />
            </div>
          </CardContent>
        </Card>

        {/* Return Section */}
        <Card className={`rounded-sm border-slate-200 ${!isDeliveryComplete ? "opacity-50" : ""}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <RotateCcw className="h-4 w-4 text-purple-500" />
                Return
              </CardTitle>
              {isReturnComplete && (
                <Badge className="bg-emerald-100 text-emerald-700 rounded-sm">
                  <Check className="h-3 w-3 mr-1" /> Complete
                </Badge>
              )}
              {isDeliveryComplete && !isReturnComplete && jobCard.status !== "pending_return" && (
                <Button size="sm" variant="outline" onClick={handleScheduleReturn} className="rounded-sm">
                  Schedule Return
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isDeliveryComplete ? (
              <p className="text-sm text-slate-400">Complete delivery signatures first</p>
            ) : (
              <>
                {/* Condition on return */}
                <div className="space-y-2">
                  <Label className="text-sm">Condition on Return</Label>
                  {canEdit && !isReturnComplete ? (
                    <Select
                      value={jobCard.condition_on_return || "good"}
                      onValueChange={(v) => handleUpdateField("condition_on_return", v)}
                    >
                      <SelectTrigger className="w-48 rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITION_OPTIONS.map((c) => (
                          <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className="rounded-sm capitalize">
                      {jobCard.condition_on_return || "Not recorded"}
                    </Badge>
                  )}
                </div>

                {/* Return Notes */}
                <div className="space-y-2">
                  <Label className="text-sm">Return Notes</Label>
                  {canEdit && !isReturnComplete ? (
                    <Textarea
                      value={jobCard.return_notes || ""}
                      onChange={(e) => setJobCard((prev) => ({ ...prev, return_notes: e.target.value }))}
                      onBlur={(e) => handleUpdateField("return_notes", e.target.value)}
                      placeholder="Notes about the return..."
                      rows={2}
                      className="rounded-sm"
                    />
                  ) : (
                    <p className="text-sm text-slate-600">{jobCard.return_notes || "No notes"}</p>
                  )}
                </div>

                {/* Return Signatures */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <SignatureBlock
                    label="Technician Signature"
                    signature={jobCard.return_technician_signature}
                    canSign={canEdit && (jobCard.status === "pending_return" || jobCard.status === "delivered")}
                    awaitingReason="Awaiting signature"
                    buttonLabel="Sign as Technician"
                    onSign={() => openSignDialog("return", "technician")}
                  />
                  <SignatureBlock
                    label="Customer Signature"
                    signature={jobCard.return_customer_signature}
                    canSign={(canEdit || canCustomerSign) && (jobCard.status === "pending_return" || jobCard.status === "delivered") && jobCard.return_technician_signature?.confirmed}
                    awaitingReason={jobCard.return_technician_signature?.confirmed ? "Awaiting signature" : "Technician must sign first"}
                    buttonLabel={isCustomer ? "Sign as Customer" : "Customer Sign"}
                    onSign={() => openSignDialog("return", "customer")}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Timeline */}
        {jobCard.timeline?.length > 0 && (
          <Card className="rounded-sm border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-500" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {jobCard.timeline.map((entry) => (
                  <div key={`${entry.timestamp}-${entry.action}`} className="flex items-start gap-3 text-sm">
                    <div className="w-2 h-2 bg-slate-300 rounded-full mt-1.5 shrink-0"></div>
                    <div>
                      <p className="font-medium text-slate-700">{entry.action}</p>
                      <p className="text-xs text-slate-400">
                        {entry.user_name} • {new Date(entry.timestamp).toLocaleString()}
                      </p>
                      {entry.notes && <p className="text-xs text-slate-500 mt-0.5">{entry.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Signature Dialog */}
      <Dialog open={signDialog.open} onOpenChange={(open) => !open && setSignDialog({ open: false, section: null, signer: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {signDialog.signer === "technician" ? "Technician" : "Customer"} Signature — {signDialog.section === "delivery" ? "Delivery" : "Return"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={signName}
                onChange={(e) => setSignName(e.target.value)}
                placeholder="Enter full name"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>Signature</Label>
              <div className="border rounded-sm bg-white">
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={150}
                  className="w-full cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <Button variant="ghost" size="sm" onClick={initCanvas} className="text-xs">
                Clear
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignDialog({ open: false, section: null, signer: null })} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleSign} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800">
              {saving ? "Saving..." : "Confirm Signature"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RentalJobCard;
